/**
 * The takeoff module's server state: the 202-materialization protocol
 * client-side. generate answers 200 (the materialized BOM) or 202
 * (queued); while queued we poll gently -- the worker's stream events
 * also invalidate, whichever lands first.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { typedClient as client, RequestRefused, type components } from '../../api';

export type TakeoffLineItem = components['schemas']['LinearLine'] | components['schemas']['ItemizedLine'];
export type TakeoffResult = components['schemas']['TakeoffResult'];

type TakeoffState = { queued: true } | { queued: false; result: TakeoffResult };

const QUEUED_POLL_MS = 3000;

/** The one scope the bill prices (DESIGN_PRINCIPLES 33): the URL's rung, the project id
 * named explicitly when the URL names none -- never a missing parameter meaning all. */
export function takeoffScope(projectId: string, scope: string | undefined): string {
  return scope ?? projectId;
}

/** The query key: the project AND the scope, so the viewer narrowing the rung in the URL
 * changes the bill without either module importing the other (FRONTEND law 3). */
export function takeoffKey(projectId: string, scope: string | undefined) {
  return ['takeoff', projectId, takeoffScope(projectId, scope)] as const;
}

export function useTakeoff(projectId: string, scope?: string) {
  return useQuery<TakeoffState>({
    queryKey: takeoffKey(projectId, scope),
    queryFn: async () => {
      const response = await client.POST('/v1/takeoff/generate', {
        body: { project_id: projectId, scope: takeoffScope(projectId, scope), refresh: false },
      });

      // Handle 202 queued response (not in typed schema, so check raw status)
      if (response.response?.status === 202) {
        return { queued: true };
      }

      // Check for server errors (4xx, 5xx) and throw RequestRefused
      if (response.response && !response.response.ok) {
        const message = response.response.statusText;
        throw new RequestRefused(response.response.status, message);
      }

      // Handle 200 success response with typed data
      if (response.data) {
        return { queued: false, result: response.data };
      }

      throw new RequestRefused(response.response?.status ?? 500, 'unexpected takeoff response');
    },
    refetchInterval: (query) => (query.state.data?.queued ? QUEUED_POLL_MS : false),
    // The queued poll must run even when the tab is unfocused --
    // "this page updates itself" is a promise, not a focus-gated one.
    refetchIntervalInBackground: true,
  });
}

/** Re-materialize with current prices (refresh: true bypasses the
 * cached run); the query refetches on settle. */
export function useRefreshTakeoff(projectId: string, scope?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await client.POST('/v1/takeoff/generate', {
        body: { project_id: projectId, scope: takeoffScope(projectId, scope), refresh: true },
      });

      // Handle 202 queued response (not in typed schema, so check raw status)
      if (response.response?.status === 202) {
        return 202;
      }

      // Check for server errors (4xx, 5xx)
      if (response.response && !response.response.ok) {
        const message = response.response.statusText;
        throw new RequestRefused(response.response.status, message);
      }

      return response.response?.status ?? 200;
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: takeoffKey(projectId, scope) }),
  });
}
